import { useState, useEffect } from 'react'
import { ClipboardList, CalendarDays, Home, Filter, FileText, Trash2, ChevronDown, ChevronRight, FileMinus, Archive, Send } from 'lucide-react'
import BasculeEspace from '../components/BasculeEspace'
import { DS } from '../lib/ui'
import { fmtE, MONTHS, propDisplayName } from '../lib/format'
import { invoiceTypeLabel, isDraftInvoice, invoiceDisplayNumber } from '../lib/invoices'
import { openFactureSnapshot } from '../lib/facture-snapshot'
import { statutFacture } from '../lib/facture-statut'
import { getAllLogsFlat, resolveLogementNom } from '../lib/sousloc-helpers'

// Mois courant au format "Avril 2026" (cohérent avec f.mois côté factLog).
const currentMonthLabel = () => {
  const now = new Date()
  return `${MONTHS[now.getMonth()]} ${now.getFullYear()}`
}

// Carte fermée / non cliquée = bleu clair (DS.cielSoft) + bordure ciel ; l'état
// déplié (marine) est appliqué au niveau des en-têtes de groupe (toggle).
const cardStyle = {background:DS.cielSoft,border:"1px solid #cfe0f2",borderRadius:16,boxShadow:"0 1px 4px rgba(33,83,135,.06), 0 4px 16px rgba(33,83,135,.08)",overflow:"hidden",marginBottom:12}
const sectionTitle = (t) => <div style={{fontFamily:DS.fontDisplay,fontSize:16,fontWeight:700,color:DS.navy,marginBottom:14}}>{t}</div>

// Parser "Avril 2026" → { year, monthIdx }. Fallback {0,-1} si invalide.
const parseMois = (s) => {
  if (!s) return { year: 0, monthIdx: -1 }
  const parts = String(s).trim().split(' ')
  const monthIdx = MONTHS.indexOf(parts[0])
  const year = parseInt(parts[1], 10) || 0
  return { year, monthIdx }
}

// Parser date FR "DD/MM/YYYY" → Date (pour tri vue Tout).
const parseDateFR = (s) => {
  if (!s) return new Date(0)
  const [d, m, y] = String(s).split('/')
  return new Date(parseInt(y, 10) || 0, (parseInt(m, 10) || 1) - 1, parseInt(d, 10) || 1)
}

const VIEWS = [
  { id: 'tout', label: 'Tout', Icon: ClipboardList },
  { id: 'parmois', label: 'Par mois', Icon: CalendarDays },
  { id: 'parlog', label: 'Par logement', Icon: Home },
]


const selectStyle = {
  background: DS.white,
  border: '1px solid #dde6f0',
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: 12,
  fontFamily: DS.fontBody,
  color: '#444',
  cursor: 'pointer',
}

// `factLog` = ici les factures dérivées de la TABLE invoices (mappées en shape
// { num, mois, prop, log, logId, montant, date, emailEnvoye, invoiceId, prefix,
// kind } par App.jsx), PAS le KV. Le bouton « Émettre » d'un brouillon appelle
// onEmettre(f) (modale App, puis setInvoiceDelivered en mode emettreSeulement),
// la suppression onDelete(f) (BROUILLON seulement : une pièce émise n'a pas de
// corbeille, elle s'annule par avoir), l'avoir onAvoir(f) (modale
// App qui annonce les trois faits, puis action serveur emitCreditNote).
// `archive` = les pièces ANNULÉES par avoir + les AVOIRS (même shape) : rendues
// en bas d'écran, pour que l'on VOIE qu'un avoir n'efface rien. Lecture seule
// sur l'originale ; sur l'avoir, UN geste : onSendAvoir(f) (modale App qui dit
// quelle pièce part, à qui, et que l'originale reste chez le propriétaire — puis
// envoi EmailJS PUIS setInvoiceDelivered sur l'avoir, jamais sur l'originale).
// Le LOGEMENT d'une ligne d'historique (29/09). Avant : `f.log || f.prop` —
// sans nom figé, la ligne affichait le nom du PROPRIÉTAIRE (ou du voyageur,
// en série V) à la place du logement. Désormais : le nom ACTUEL du logement
// (l'affichage suit le vivant, ADR-205 — la pièce émise, elle, reste figée),
// puis le nom figé à l'émission, puis un tiret. Jamais une personne, jamais
// un code technique.
export function nomLogementDeLigne(cfg, f) {
  const id = f && f.logId
  if (id) {
    const actuel = resolveLogementNom(cfg, id)
    if (actuel && actuel !== id) return actuel
  }
  const fige = f && typeof f.log === "string" ? f.log.trim() : ""
  if (fige && fige !== id) return fige
  return "—"
}

export default function HistoriquePanel({ cfg, factLog, factLogKv = [], archive = [], onEmettre, onEnvoyer, onEssai, onDelete, onAvoir, onSendAvoir }) {
  const [view, setView] = useState('parmois')
  const [filtreStatut, setFiltreStatut] = useState('all')
  const [filtreAnnee, setFiltreAnnee] = useState('all')
  // Tri configurable vue Tout uniquement.
  const [sortKey, setSortKey] = useState('date')
  const [sortDir, setSortDir] = useState('desc')
  // Groupes dépliés (clés = f.mois en vue parmois, f.log en vue parlog).
  // Reset au switch de vue : parmois → mois courant déplié, parlog → tous repliés.
  const [expanded, setExpanded] = useState(() => new Set([currentMonthLabel()]))
  // Section « Annulées et avoirs » : repliée par défaut (lecture seule).
  const [archiveOpen, setArchiveOpen] = useState(false)

  useEffect(() => {
    if (view === 'parmois') setExpanded(new Set([currentMonthLabel()]))
    else if (view === 'parlog') setExpanded(new Set())
  }, [view])

  const toggleExpand = (k) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k); else next.add(k)
      return next
    })
  }

  // Années auto-générées depuis factLog (tri descendant)
  const annees = [...new Set(factLog.map(f => parseMois(f.mois).year).filter(y => y > 0))].sort((a, b) => b - a)

  // Pipeline filtres (statut + année) — appliqué AVANT groupement et tri
  const factFiltered = factLog.filter(f => {
    // Filtre sur le statut RÉEL (2026-09-30) : « Envoyé » = un courriel est
    // parti (statutFacture), plus delivered_at.
    const code = statutFacture(f, factLogKv).code
    const partie = code === 'envoyee' || code === 'transmise'
    if (filtreStatut === 'sent' && !partie) return false
    if (filtreStatut === 'unsent' && partie) return false
    // Journal des envois illisible : ni « envoyée » ni « non envoyée ».
    if (filtreStatut !== 'all' && (code === 'inconnu' || code === 'incertain')) return false
    if (filtreAnnee !== 'all' && parseMois(f.mois).year !== parseInt(filtreAnnee, 10)) return false
    return true
  })

  // « Total émis » = SOMME DES PIÈCES RÉELLEMENT ÉMISES. Un BROUILLON (chantier
  // « numéro à l'envoi ») n'a consommé aucun numéro, n'a été transmis à personne
  // et reste supprimable : l'agréger sous un libellé « émis » afficherait un
  // chiffre d'affaires facturé qui n'existe pas. On l'isole sur sa propre ligne.
  const factEmises = factFiltered.filter(f => !isDraftInvoice(f))
  const factBrouillons = factFiltered.filter(f => isDraftInvoice(f))
  const total = factEmises.reduce((s, f) => s + (f.montant || 0), 0)
  const totalBrouillons = factBrouillons.reduce((s, f) => s + (f.montant || 0), 0)

  // Composant ligne facture — réutilisé dans toutes les vues.
  // Chantier D2 : plus d'index positionnel. Toggle et suppression ciblent
  // l'entrée par IDENTITÉ (invoiceId, fallback num legacy) → robuste au
  // réordonnancement du factLog (une émission concurrente prepend en tête).
  const Row = ({ f }) => (
    <div style={{...cardStyle,background:DS.white,border:"none",padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <div style={{flex:1}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {/* Chantier « numéro à l'envoi » : un BROUILLON n'a PAS de numéro
              (invoice_number NULL en base). Libellé explicite, jamais un numéro
              factice. La règle « qu'est-ce qu'un brouillon » et le libellé
              viennent tous deux de src/lib/invoices.js — source unique, pas de
              test d'absence de numéro réécrit à la main dans l'UI. */}
          {isDraftInvoice(f) ? (
            <div style={{fontFamily:DS.fontDisplay,fontWeight:700,color:DS.amber,fontSize:14}} title="Aucun numéro consommé — supprimable et régénérable sans trouer la numérotation">✎ {invoiceDisplayNumber(f)}</div>
          ) : (
            <div style={{fontFamily:DS.fontDisplay,fontWeight:700,color:DS.navy,fontSize:14}}>N° {invoiceDisplayNumber(f)}</div>
          )}
          {/* BUG 37 — distingue Reçu (émetteur proprio) de Facture sans les agréger. */}
          {f.kind && (
            <span style={{fontSize:9,background:f.kind==='receipt'?"#eef3fb":"#f2f4f7",color:f.kind==='receipt'?DS.skyDeep:DS.navy,borderRadius:20,padding:"2px 7px",fontWeight:700,fontFamily:DS.fontBody}}>
              {invoiceTypeLabel(f.kind)}
            </span>
          )}
          {/* 2026-09-30 — plus de pastille « Non envoyé » à cliquer. Elle posait
              la date de transmission À LA MAIN et, sur un brouillon, attribuait
              le numéro sans le dire. Désormais :
                · brouillon → un vrai bouton « Émettre » (numéro attribué, aucun
                  courriel) ;
                · pièce émise → son statut RÉEL, non cliquable : « Envoyé »
                  seulement si un courriel est parti (statutFacture). */}
          {(() => {
            const st = statutFacture(f, factLogKv)
            if (st.code === 'brouillon') {
              return typeof onEmettre === 'function' ? (
                <button data-testid="emettre-brouillon" onClick={()=>onEmettre(f)}
                  title="Donner à ce brouillon son numéro définitif — aucun courriel ne part"
                  style={{fontSize:11,background:DS.marine,color:"#fff",borderRadius:8,padding:"4px 10px",fontWeight:700,border:"none",cursor:"pointer",fontFamily:DS.fontBody}}>
                  Émettre
                </button>
              ) : null
            }
            const envoyee = st.code === 'envoyee'
            const transmise = st.code === 'transmise'
            // « Envoyer » : facture COMMISSION (série P) numérotée, sans preuve
            // de courriel, journal des envois LU (illisible → « — », pas
            // d'« Envoyer » : on ne sait pas si elle est déjà partie). Même
            // fenêtre de relecture ; document figé, aucun
            // numéro tiré (App.jsx → envoyerFactureExistante).
            const peutEnvoyer = !envoyee && !f.horsApplication && f.prefix === 'P' && f.kind !== 'credit_note' && st.code !== 'inconnu' && st.code !== 'incertain' && st.code !== 'transmise' && typeof onEnvoyer === 'function'
            // ESSAI à Dimitri seul (2026-10-03) : toute facture P émise DANS
            // l'application, même déjà envoyée ou transmise — il ne va qu'à lui
            // et n'écrit rien (lib/facture-envoi-proprio.js, envoyerEssai).
            const peutEssai = !f.horsApplication && f.prefix === 'P' && f.kind !== 'credit_note' && typeof onEssai === 'function'
            return (
              <>
                <span data-testid="statut-facture"
                  title={transmise ? "Émise, mais l'application ne sait pas si un courriel est parti (factures anciennes, ou marquées à la main)" : undefined}
                  style={{fontSize:9,background:envoyee?"#edf7f0":(transmise?"#eef3fb":"#fff8e6"),color:envoyee?DS.green:(transmise?DS.skyDeep:DS.amber),borderRadius:20,padding:"2px 7px",fontWeight:700,fontFamily:DS.fontBody}}>
                  {envoyee ? '✓ Envoyé' : st.libelle}
                </span>
                {peutEnvoyer && (
                  <button data-testid="envoyer-facture" onClick={()=>onEnvoyer(f)}
                    title="Relire le courriel puis envoyer cette facture au propriétaire — aucun numéro n'est tiré"
                    style={{fontSize:10,background:"none",border:`1px solid ${DS.navy}`,color:DS.navy,borderRadius:8,padding:"2px 8px",fontWeight:700,cursor:"pointer",fontFamily:DS.fontBody,display:"inline-flex",alignItems:"center",gap:3}}>
                    <Send size={12} strokeWidth={2} color={DS.navy}/>Envoyer
                  </button>
                )}
                {peutEssai && (
                  <button data-testid="essai-facture" onClick={()=>onEssai(f)}
                    title="M'envoyer cette facture à moi seul, pour voir le courriel — le propriétaire ne reçoit rien, rien n'est noté"
                    style={{fontSize:10,background:"none",border:`1px dashed ${DS.navy}`,color:DS.navy,borderRadius:8,padding:"2px 8px",fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody}}>
                    🧪 Essai
                  </button>
                )}
              </>
            )
          })()}
        </div>
        <div style={{fontSize:11,color:"#888",marginTop:3}}>{nomLogementDeLigne(cfg, f)} · {f.mois} · {f.date}{f.emailDest&&` · ${f.emailDest}`}</div>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <div style={{fontFamily:DS.fontDisplay,fontSize:17,fontWeight:700,color:DS.skyDeep}}>{fmtE(f.montant)}</div>
        {(() => {
          // Disponible si snapshot KV OU invoiceId (fallback table invoices).
          const canShow = !!(f.htmlSnapshot || f.invoiceId)
          // Vue mixte (reçus + factures + commissions) : libellé dérivé du type
          // réel de la ligne (f.kind). Sans kind connu → terme neutre « document ».
          const docTerm = f.kind ? invoiceTypeLabel(f.kind).toLowerCase() : 'document'
          return (
            <button onClick={()=>openFactureSnapshot(f)}
              disabled={!canShow}
              title={canShow ? `Re-télécharger le ${docTerm}` : `Snapshot indisponible — ${docTerm} pré-migration`}
              style={{background:"none",border:"none",cursor:canShow?"pointer":"not-allowed",padding:4,display:"inline-flex",alignItems:"center",opacity:canShow?1:0.4}}><FileText size={16} strokeWidth={1.9} color={canShow?DS.marine:"#ccc"}/></button>
          )
        })()}
        {/* AVOIR — le geste légal sur une pièce ÉMISE (numérotée). Jamais sur
            un brouillon (il se supprime, rien n'a été consommé) ni sur un avoir. */}
        {!isDraftInvoice(f) && f.kind !== 'credit_note' && typeof onAvoir === 'function' && (
          <button onClick={()=>onAvoir(f)} title="Annuler par un avoir (pièce neuve AV-* ; l'originale reste)" aria-label={`Annuler la facture ${f.num} par un avoir`}
            style={{background:"none",border:"none",cursor:"pointer",padding:4,display:"inline-flex",alignItems:"center",gap:3,fontSize:10,fontWeight:700,color:DS.navy,fontFamily:DS.fontBody}}><FileMinus size={16} strokeWidth={1.9} color={DS.navy}/>Avoir</button>
        )}
        {/* CORBEILLE — sur un BROUILLON seulement (sans numéro). Une pièce
            émise ne s'efface jamais (CGI 289) : seul « Avoir » l'annule. */}
        {isDraftInvoice(f) && (
          <button onClick={()=>onDelete(f)} title="Supprimer"
            style={{background:"none",border:"none",cursor:"pointer",padding:4,display:"inline-flex",alignItems:"center"}}><Trash2 size={16} strokeWidth={1.9} color={DS.textMuted}/></button>
        )}
      </div>
    </div>
  )

  // Ligne ARCHIVE (annulée / avoir). L'ORIGINALE est en lecture seule — ni
  // « envoyé », ni suppression, ni avoir : elle ne bouge plus. L'AVOIR porte
  // son état de transmission et UN geste, l'envoi au propriétaire. Chaque
  // pièce nomme sa contrepartie ; sur l'originale, « le propriétaire a reçu
  // l'explication » se LIT sur l'avoir lié (delivered_at), rien n'est écrit
  // sur elle pour le dire.
  const avoirParOriginale = new Map(archive.filter(a => a.kind === 'credit_note' && a.relatedInvoiceId).map(a => [a.relatedInvoiceId, a]))
  const ArchiveRow = ({ f }) => {
    const estAvoir = f.kind === 'credit_note'
    const avoirLie = estAvoir ? null : (avoirParOriginale.get(f.invoiceId) || null)
    const contrepartie = estAvoir ? f.originalNumber : (avoirLie?.num || null)
    return (
      <div data-testid="archive-row" style={{...cardStyle,background:DS.white,border:"none",padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",opacity:.92}}>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <div style={{fontFamily:DS.fontDisplay,fontWeight:700,color:estAvoir?DS.navy:DS.textMuted,fontSize:14,textDecoration:estAvoir?"none":"line-through"}}>N° {invoiceDisplayNumber(f)}</div>
            <span style={{fontSize:9,background:estAvoir?"#eef3fb":"#fdecec",color:estAvoir?DS.skyDeep:DS.red,borderRadius:20,padding:"2px 7px",fontWeight:700,fontFamily:DS.fontBody}}>
              {estAvoir ? 'Avoir' : 'Annulée par avoir'}
            </span>
            {contrepartie && <span style={{fontSize:10,color:"#666",fontFamily:DS.fontBody}}>{estAvoir ? `annule ${contrepartie}` : `→ ${contrepartie}`}</span>}
            {/* État de transmission de l'AVOIR : posé par l'envoi (jamais à la
                main — c'est l'envoi qui prouve la transmission). */}
            {estAvoir && (
              <span data-testid="avoir-transmission" style={{fontSize:9,background:f.emailEnvoye?"#edf7f0":"#fff8e6",color:f.emailEnvoye?DS.green:DS.amber,borderRadius:20,padding:"2px 7px",fontWeight:700,fontFamily:DS.fontBody}}>
                {f.emailEnvoye ? '✓ Transmis au propriétaire' : 'Non transmis'}
              </span>
            )}
            {/* Sur l'ORIGINALE : l'explication est-elle arrivée ? Dérivé de
                l'avoir lié, sans rien écrire sur elle. */}
            {avoirLie && (
              <span data-testid="originale-explication" style={{fontSize:9,background:avoirLie.emailEnvoye?"#edf7f0":"#fff8e6",color:avoirLie.emailEnvoye?DS.green:DS.amber,borderRadius:20,padding:"2px 7px",fontWeight:700,fontFamily:DS.fontBody}}>
                {avoirLie.emailEnvoye ? 'avoir transmis' : 'avoir non transmis'}
              </span>
            )}
          </div>
          <div style={{fontSize:11,color:"#888",marginTop:3}}>{nomLogementDeLigne(cfg, f)} · {f.mois} · {f.date}{!estAvoir && ' · conservée, inchangée'}{estAvoir&&f.emailDest&&` · ${f.emailDest}`}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{fontFamily:DS.fontDisplay,fontSize:15,fontWeight:700,color:estAvoir?DS.red:DS.textMuted}}>{fmtE(f.montant)}</div>
          <button onClick={()=>openFactureSnapshot(f)} disabled={!f.invoiceId} title="Voir le document"
            style={{background:"none",border:"none",cursor:f.invoiceId?"pointer":"not-allowed",padding:4,display:"inline-flex",alignItems:"center"}}><FileText size={16} strokeWidth={1.9} color={DS.marine}/></button>
          {estAvoir && typeof onSendAvoir === 'function' && (
            <button onClick={()=>onSendAvoir(f)} title={f.emailEnvoye ? "Renvoyer l'avoir au propriétaire (la date de première transmission est conservée)" : "Envoyer l'avoir au propriétaire (envoi, puis date de transmission)"}
              aria-label={`Envoyer l'avoir ${f.num} au propriétaire`}
              style={{background:"none",border:"none",cursor:"pointer",padding:4,display:"inline-flex",alignItems:"center",gap:3,fontSize:10,fontWeight:700,color:DS.navy,fontFamily:DS.fontBody}}><Send size={16} strokeWidth={1.9} color={DS.navy}/>{f.emailEnvoye ? 'Renvoyer' : 'Envoyer'}</button>
          )}
        </div>
      </div>
    )
  }

  // En-tête de groupe cliquable (vues parmois/parlog).
  // Règle #14 — en-tête de groupe OUVERT = marine plein + texte/chevron blanc ;
  // FERMÉ = blanc + titre marine.
  const GroupHeader = ({ k, title, count, totalGroup, isExpanded }) => (
    <div onClick={() => toggleExpand(k)}
      style={{padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",background:isExpanded?DS.marine:DS.cielSoft,borderBottom:isExpanded?"1px solid rgba(255,255,255,.15)":"none",userSelect:"none"}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{width:14,display:"inline-flex",alignItems:"center"}}>{isExpanded?<ChevronDown size={15} strokeWidth={2.2} color="#fff"/>:<ChevronRight size={15} strokeWidth={2.2} color={DS.marine}/>}</span>
        <span style={{fontFamily:DS.fontDisplay,fontWeight:700,color:isExpanded?"#fff":DS.navy,fontSize:15}}>{title}</span>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:14}}>
        <span style={{fontSize:11,color:isExpanded?"rgba(255,255,255,.7)":"#888",fontFamily:DS.fontBody}}>{count} document{count>1?'s':''}</span>
        <span style={{fontFamily:DS.fontDisplay,fontWeight:700,color:isExpanded?"#fff":DS.skyDeep,fontSize:15}}>{fmtE(totalGroup)}</span>
      </div>
    </div>
  )

  let content
  if (view === 'parmois') {
    // Group by f.mois, tri chronologique inverse (plus récent en haut).
    const groups = new Map()
    factFiltered.forEach(f => {
      const k = f.mois || '?'
      if (!groups.has(k)) groups.set(k, [])
      groups.get(k).push(f)
    })
    const sorted = [...groups.entries()].sort(([a], [b]) => {
      const pa = parseMois(a), pb = parseMois(b)
      if (pb.year !== pa.year) return pb.year - pa.year
      return pb.monthIdx - pa.monthIdx
    })
    content = sorted.map(([mois, facts]) => {
      const isExpanded = expanded.has(mois)
      const totalGroup = facts.reduce((s, f) => s + (f.montant || 0), 0)
      return (
        <div key={mois} style={cardStyle}>
          <GroupHeader k={mois} title={mois} count={facts.length} totalGroup={totalGroup} isExpanded={isExpanded} />
          {isExpanded && (
            <div style={{padding:"8px 8px 4px"}}>
              {facts.map(f => <Row key={f.rowKey || f.num} f={f} />)}
            </div>
          )}
        </div>
      )
    })
  } else if (view === 'parlog') {
    // Group by f.logId si présent (post-fix F3) sinon f.log (compat legacy).
    // Le label affiché = nom actuel du log dans cfg (suit les renommages).
    // Tri alphabétique sur le label affiché.
    const allLogsById = new Map(getAllLogsFlat(cfg).map(l => [l.id, l]))
    const groups = new Map()
    factFiltered.forEach(f => {
      const k = f.logId || f.log || f.prop || '—'
      if (!groups.has(k)) groups.set(k, [])
      groups.get(k).push(f)
    })
    const enriched = [...groups.entries()].map(([k, facts]) => {
      const logFromCfg = allLogsById.get(k)
      // Label : nom actuel cfg → nom à l'émission de la 1ère facture → clé brute
      const label = logFromCfg?.nom || facts[0]?.log || k
      return { k, label, facts, logFromCfg }
    })
    const sorted = enriched.sort((a, b) => a.label.localeCompare(b.label, 'fr'))
    content = sorted.map(({ k, label, facts, logFromCfg }) => {
      const isExpanded = expanded.has(k)
      const totalGroup = facts.reduce((s, f) => s + (f.montant || 0), 0)
      // Lookup proprio via cfg : si log encore présent, prendre le proprio
      // associé. Sinon fallback sur f.prop figé à l'émission.
      const prop = logFromCfg
        ? cfg?.props?.find(p => p.logs?.some(l => l.id === k || l.chambres?.some(c => c.id === k)))
        : null
      const propLabel = prop ? propDisplayName(prop) : (facts[0]?.prop || '—')
      return (
        <div key={k} style={cardStyle}>
          <div onClick={() => toggleExpand(k)}
            style={{padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",background:isExpanded?DS.marine:DS.cielSoft,borderBottom:isExpanded?"1px solid rgba(255,255,255,.15)":"none",userSelect:"none"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:8,flex:1}}>
              <span style={{width:14,display:"inline-flex",alignItems:"center",marginTop:3}}>{isExpanded?<ChevronDown size={15} strokeWidth={2.2} color="#fff"/>:<ChevronRight size={15} strokeWidth={2.2} color={DS.marine}/>}</span>
              <div>
                <div style={{fontFamily:DS.fontDisplay,fontWeight:700,color:isExpanded?"#fff":DS.navy,fontSize:15}}>{label}</div>
                <div style={{fontSize:11,color:isExpanded?"rgba(255,255,255,.7)":"#888",marginTop:3}}>{propLabel} · {facts.length} document{facts.length>1?'s':''} · <span style={{color:isExpanded?"#fff":DS.skyDeep,fontWeight:700}}>{fmtE(totalGroup)}</span></div>
              </div>
            </div>
          </div>
          {isExpanded && (
            <div style={{padding:"8px 8px 4px"}}>
              {facts.map(f => <Row key={f.rowKey || f.num} f={f} />)}
            </div>
          )}
        </div>
      )
    })
  } else {
    // view === 'tout' — liste plate filtrée + tri configurable
    const sorted = [...factFiltered].sort((a, b) => {
      let cmp = 0
      if (sortKey === 'date') cmp = parseDateFR(a.date) - parseDateFR(b.date)
      else if (sortKey === 'num') cmp = String(a.num || '').localeCompare(String(b.num || ''))
      else if (sortKey === 'montant') cmp = (a.montant || 0) - (b.montant || 0)
      return sortDir === 'desc' ? -cmp : cmp
    })
    content = sorted.map(f => <Row key={f.rowKey || f.num} f={f} />)
  }

  return (
    <>
      {/* « Documents émis » → « Historique » (lot 5, décision Dimitri
          2026-08-28 : même écran, nom plus clair). */}
      {sectionTitle("Historique")}

      {/* Onglets de vue — RÈGLE DU DESSIN UNIQUE (Dimitri 2026-08-19) : la
          bascule de la nav, partout. */}
      <div style={{marginBottom:12}}>
        <BasculeEspace testId="historique-vue" actif={view} onChange={setView}
          entrees={VIEWS.map(v => ({ id: v.id, label: v.label, icone: v.Icon }))} />
      </div>

      {/* Filtres communs + tri (vue Tout) */}
      <div style={{display:'flex',gap:10,marginBottom:14,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{color:DS.textSecondary,fontSize:12,fontWeight:600,display:"inline-flex",alignItems:"center",gap:5}}><Filter size={13} strokeWidth={2}/> Filtres :</span>
        <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)} style={selectStyle}>
          <option value="all">Tous statuts</option>
          <option value="sent">✓ Envoyé, ou envoi non suivi</option>
          <option value="unsent">Non envoyée, ou brouillon</option>
        </select>
        <select value={filtreAnnee} onChange={e => setFiltreAnnee(e.target.value)} style={selectStyle}>
          <option value="all">Toutes années</option>
          {annees.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        {view === 'tout' && (
          <>
            <span style={{color:'#666',fontSize:12,fontWeight:600,marginLeft:8}}>Tri :</span>
            <select value={sortKey} onChange={e => setSortKey(e.target.value)} style={selectStyle}>
              <option value="date">Date</option>
              <option value="num">Numéro</option>
              <option value="montant">Montant</option>
            </select>
            <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
              title={sortDir === 'desc' ? 'Décroissant — cliquer pour inverser' : 'Croissant — cliquer pour inverser'}
              style={{...selectStyle,fontWeight:700,color:DS.navy,minWidth:32}}>
              {sortDir === 'desc' ? '↓' : '↑'}
            </button>
          </>
        )}
      </div>

      {factLog.length === 0 ? (
        <div style={{...cardStyle,padding:32,textAlign:"center",color:"#bbb",fontSize:13}}>
          <div style={{fontFamily:DS.fontDisplay,fontSize:16,color:"#ddd",marginBottom:6}}>Aucune facture émise</div>
        </div>
      ) : factFiltered.length === 0 ? (
        <div style={{...cardStyle,padding:32,textAlign:"center",color:"#bbb",fontSize:13}}>
          <div style={{fontFamily:DS.fontDisplay,fontSize:14,color:"#aaa"}}>Aucune facture pour les filtres sélectionnés</div>
        </div>
      ) : content}

      {factEmises.length > 0 && (
        <div style={{background:DS.marine,borderRadius:DS.radiusCard,padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}}>
          <span style={{fontWeight:700,color:DS.cielSoft,fontSize:13}}>Total émis ({factEmises.length})</span>
          <span style={{fontFamily:DS.fontDisplay,fontWeight:700,color:"#fff",fontSize:17}}>{fmtE(total)}</span>
        </div>
      )}

      {/* ARCHIVE — annulées par avoir + avoirs. Jamais dans le « Total émis »
          (pièces actives seulement, décision Dimitri) : ici on MONTRE que
          l'originale est toujours là, à côté de sa contrepartie. */}
      {archive.length > 0 && (
        <div data-testid="archive-section" style={{...cardStyle,marginTop:12}}>
          <div onClick={() => setArchiveOpen(o => !o)}
            style={{padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",background:archiveOpen?DS.marine:DS.cielSoft,userSelect:"none"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{width:14,display:"inline-flex",alignItems:"center"}}>{archiveOpen?<ChevronDown size={15} strokeWidth={2.2} color="#fff"/>:<ChevronRight size={15} strokeWidth={2.2} color={DS.marine}/>}</span>
              <Archive size={14} strokeWidth={2} color={archiveOpen?"#fff":DS.marine}/>
              <span style={{fontFamily:DS.fontDisplay,fontWeight:700,color:archiveOpen?"#fff":DS.navy,fontSize:14}}>Annulées et avoirs</span>
            </div>
            <span style={{fontSize:11,color:archiveOpen?"rgba(255,255,255,.7)":"#888",fontFamily:DS.fontBody}}>{archive.length} pièce{archive.length>1?'s':''} · hors total émis</span>
          </div>
          {archiveOpen && (
            <div style={{padding:"8px 8px 4px"}}>
              {archive.map(f => <ArchiveRow key={f.rowKey || f.num} f={f} />)}
            </div>
          )}
        </div>
      )}

      {/* Ligne SÉPARÉE : les brouillons ne sont pas émis (aucun numéro consommé,
          rien transmis). Jamais agrégés au total ci-dessus. */}
      {factBrouillons.length > 0 && (
        <div style={{background:"#fff8e6",border:`1px solid ${DS.amber}`,borderRadius:DS.radiusCard,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
          <span style={{fontWeight:700,color:DS.amber,fontSize:12}}>
            ✎ dont {factBrouillons.length} brouillon{factBrouillons.length > 1 ? 's' : ''} — non émis, aucun numéro consommé
          </span>
          <span style={{fontFamily:DS.fontDisplay,fontWeight:700,color:DS.amber,fontSize:15}}>{fmtE(totalBrouillons)}</span>
        </div>
      )}
    </>
  )
}
